/**
 * API-клиент воркспейсов, участников и action-based доступов (бизнес-домен colver).
 *
 * Модуль работает ТОЛЬКО с реальным backend (моки сняты): эндпоинты
 * `/workspaces*` живут в том же namespace, что и meerec (`VITE_MEEREC_BASE`).
 * Авторизация в dev — bcf tests-wrapper (`X-Tests-User-Sid` из `colver:auth`),
 * в проде — Gatekeeper-сессия.
 */
import axios from 'axios';
import {readStoredSid} from '@api/auth';
import type {SetPasswordInput, SetPasswordResult} from '@api/users';
import type {
  AccessDecision,
  ActionDefinition,
  AddWorkspaceMemberInput,
  CreateWorkspaceInput,
  GrantActionInput,
  GrantSummary,
  MyAccess,
  RoleDefinition,
  UpdateWorkspaceInput,
  UpdateWorkspaceMemberInput,
  Workspace,
  WorkspaceBranch,
  WorkspaceMember,
  WorkspaceSummary,
} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

export const workspacesClient = {
  // ── воркспейсы ──────────────────────────────────────────────────────────────

  /** Список воркспейсов текущего пользователя (источник свитчера). */
  async listMyWorkspaces(): Promise<WorkspaceSummary[]> {
    const {data} = await http.post<WorkspaceSummary[]>('/workspaces', {});
    return data;
  },

  /** Все воркспейсы (операторская консоль). */
  async listAllWorkspaces(): Promise<Workspace[]> {
    const {data} = await http.post<Workspace[]>('/workspaces/all', {
      limit: 500,
    });
    return data;
  },

  /** Создать воркспейс клиента (оператор). */
  async createWorkspace(input: CreateWorkspaceInput): Promise<Workspace> {
    const {data} = await http.put<Workspace>('/workspaces', input);
    return data;
  },

  /** Получить воркспейс. */
  async getWorkspace(workspaceSid: string): Promise<Workspace> {
    const {data} = await http.get<Workspace>(`/workspaces/${workspaceSid}`);
    return data;
  },

  /** Изменить воркспейс. */
  async updateWorkspace(
    workspaceSid: string,
    input: UpdateWorkspaceInput,
  ): Promise<Workspace> {
    const {data} = await http.patch<Workspace>(
      `/workspaces/${workspaceSid}`,
      input,
    );
    return data;
  },

  /** Удалить воркспейс (soft delete, оператор). */
  async deleteWorkspace(workspaceSid: string): Promise<void> {
    await http.delete(`/workspaces/${workspaceSid}`);
  },

  // ── филиалы ────────────────────────────────────────────────────────────────

  async listBranches(workspaceSid: string): Promise<WorkspaceBranch[]> {
    const {data} = await http.post<WorkspaceBranch[]>(
      `/workspaces/${workspaceSid}/branches`,
      {},
    );
    return data;
  },

  async getBranch(
    workspaceSid: string,
    branchSid: string,
  ): Promise<WorkspaceBranch> {
    const {data} = await http.get<WorkspaceBranch>(
      `/workspaces/${workspaceSid}/branches/${branchSid}`,
    );
    return data;
  },

  async createBranch(
    workspaceSid: string,
    name: string,
  ): Promise<WorkspaceBranch> {
    const {data} = await http.put<WorkspaceBranch>(
      `/workspaces/${workspaceSid}/branches`,
      {name},
    );
    return data;
  },

  async updateBranch(
    workspaceSid: string,
    branchSid: string,
    name: string,
  ): Promise<WorkspaceBranch> {
    const {data} = await http.patch<WorkspaceBranch>(
      `/workspaces/${workspaceSid}/branches/${branchSid}`,
      {name},
    );
    return data;
  },

  async deleteBranch(workspaceSid: string, branchSid: string): Promise<void> {
    await http.delete(`/workspaces/${workspaceSid}/branches/${branchSid}`);
  },

  // ── участники (иерархия) ───────────────────────────────────────────────────

  /** Список участников воркспейса (с данными учёток). */
  async listMembers(workspaceSid: string): Promise<WorkspaceMember[]> {
    const {data} = await http.post<WorkspaceMember[]>(
      `/workspaces/${workspaceSid}/members`,
      {},
    );
    return data;
  },

  /** Добавить участника (требует manage-members или оператора). */
  async addMember(
    workspaceSid: string,
    input: AddWorkspaceMemberInput,
  ): Promise<WorkspaceMember> {
    const {data} = await http.put<WorkspaceMember>(
      `/workspaces/${workspaceSid}/members`,
      input,
    );
    return data;
  },

  /** Изменить участника (иерархия/подпись/состояние). */
  async updateMember(
    workspaceSid: string,
    membershipSid: string,
    input: UpdateWorkspaceMemberInput,
  ): Promise<WorkspaceMember> {
    const {data} = await http.patch<WorkspaceMember>(
      `/workspaces/${workspaceSid}/members/${membershipSid}`,
      input,
    );
    return data;
  },

  /** Удалить участника (soft delete; дети поднимаются к родителю узла). */
  async removeMember(
    workspaceSid: string,
    membershipSid: string,
  ): Promise<void> {
    await http.delete(`/workspaces/${workspaceSid}/members/${membershipSid}`);
  },

  /** Выдать/сменить пароль участника (manage-members или оператор). */
  async setMemberPassword(
    workspaceSid: string,
    membershipSid: string,
    input: SetPasswordInput,
  ): Promise<SetPasswordResult> {
    const {data} = await http.post<SetPasswordResult>(
      `/workspaces/${workspaceSid}/members/${membershipSid}/password`,
      input,
    );
    return data;
  },

  // ── действия и гранты ──────────────────────────────────────────────────────

  /** Реестр действий (для форм выдачи доступов). */
  async listActions(): Promise<ActionDefinition[]> {
    const {data} = await http.get<ActionDefinition[]>('/actions');
    return data;
  },

  /** Реестр ролей-пресетов (для форм управления участниками). */
  async listRoles(): Promise<RoleDefinition[]> {
    const {data} = await http.get<RoleDefinition[]>('/roles');
    return data;
  },

  /** Гранты воркспейса (manage-access/оператор — все, участник — свои). */
  async listGrants(workspaceSid: string): Promise<GrantSummary[]> {
    const {data} = await http.post<GrantSummary[]>(
      `/workspaces/${workspaceSid}/grants`,
      {},
    );
    return data;
  },

  /** Выдать действие участнику. */
  async grantAction(
    workspaceSid: string,
    input: GrantActionInput,
  ): Promise<GrantSummary> {
    const {data} = await http.put<GrantSummary>(
      `/workspaces/${workspaceSid}/grants`,
      input,
    );
    return data;
  },

  /** Отозвать грант. */
  async revokeGrant(workspaceSid: string, grantSid: string): Promise<void> {
    await http.delete(`/workspaces/${workspaceSid}/grants/${grantSid}`);
  },

  // ── проверки доступа ───────────────────────────────────────────────────────

  /** Сводка доступов текущего пользователя в воркспейсе. */
  async myAccess(workspaceSid: string): Promise<MyAccess> {
    const {data} = await http.get<MyAccess>(
      `/workspaces/${workspaceSid}/access/my`,
    );
    return data;
  },

  /** `can user do action` — действие без целевого пользователя. */
  async canDo(workspaceSid: string, action: string): Promise<AccessDecision> {
    const {data} = await http.get<AccessDecision>(
      `/workspaces/${workspaceSid}/access/can/${action}`,
    );
    return data;
  },

  /** `can user do action for user` — действие над пользователем. */
  async canDoForUser(
    workspaceSid: string,
    action: string,
    targetUserSid: string,
  ): Promise<AccessDecision> {
    const {data} = await http.get<AccessDecision>(
      `/workspaces/${workspaceSid}/access/can/${action}/for-user/${targetUserSid}`,
    );
    return data;
  },
};
